import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Pressable } from 'react-native';
import { XStack, Spinner } from 'tamagui';
import { Volume2, VolumeOff } from '@tamagui/lucide-icons';
import ReactPlayer from 'react-player';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  handleMarketingEvents,
  KissanEventType,
  sendToFirebasEvents,
} from '@ninjacart/nc-nk-store';

type ProgressProps = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

type PodcastPlayerProps = {
  url: string;
  index: number;
  currentVisibleIndex: number;
  id?: string;
  title?: string;
  category?: string | string[];
  screenName?: string;
  onAudioComplete?: () => void;
  setCurrentPlayIndex?: React.Dispatch<React.SetStateAction<number>>;
  isMuteIconShow?: boolean;
};

export const PodcastPlayer = ({
  url,
  index,
  currentVisibleIndex,
  id,
  title,
  category,
  screenName,
  onAudioComplete,
  setCurrentPlayIndex,
  isMuteIconShow,
}: PodcastPlayerProps) => {
  const playerRef = useRef<ReactPlayer>(null);
  const durationRef = useRef<number>(-1); // total duration
  const trackedProgressRef = useRef<Set<number>>(new Set());

  const [isPlaying, setIsPlaying] = useState(false); // intended state
  const [isActuallyPlaying, setIsActuallyPlaying] = useState(false); // real state from ReactPlayer
  const [loading, setLoading] = useState(false);
  const enqueue = useQueuedEventStore((state) => state.enqueue);

  /** Event tracker */
  const AudioEvent = (event: KissanEventType, currentTime?: number) => {
    const eventParams = [
      addEventParam('id', id || ''),
      addEventParam('index', index.toString()),
      addEventParam('title', title || ''),
      addEventParam(
        'videoDuration',
        durationRef.current === -1 ? undefined : durationRef.current.toString()
      ),
      ...(currentTime !== undefined
        ? [addEventParam('videoPlaybackTime', currentTime.toString())]
        : []),
      addEventParam('selectedCategory', (category ?? []).toString()),
    ];

    const audioEvent: INinjaEvent = {
      name: event,
      screenDetails: { screen: screenName || 'Podcast Page' },
      eventParams,
    };

    enqueue(audioEvent);
    if (sendToFirebasEvents.includes(event)) {
      handleMarketingEvents(event, { screen: screenName || 'Podcast Page' }, eventParams);
    }
  };

  /** Auto play/pause when visible index changes */
  useEffect(() => {
    if (index === currentVisibleIndex) {
      setIsPlaying(true);
      AudioEvent(KissanEventType.PODCAST_PLAY);
    } else {
      if (isPlaying) {
        const currentTime = playerRef.current?.getCurrentTime?.() ?? 0;
        AudioEvent(KissanEventType.PODCAST_PAUSE, currentTime);
      }
      setIsPlaying(false);
    }
  }, [currentVisibleIndex]);

  /** Manual toggle */
  const handlePlayPause = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    if (isPlaying) {
      playerRef.current?.getInternalPlayer()?.pause?.();
      const currentTime = playerRef.current?.getCurrentTime?.() ?? 0;
      AudioEvent(KissanEventType.PODCAST_PAUSE, currentTime);
      setIsPlaying(false);
    } else {
      setCurrentPlayIndex?.(index);
      AudioEvent(KissanEventType.PODCAST_PLAY);
      setIsPlaying(true);
    }

    setLoading(false);
  }, [isPlaying, loading]);

  /** Progress tracking */
  const handleProgress = useCallback(
    (progress: ProgressProps) => {
      if (!isPlaying) return;

      // 🔹 Handle tab hidden → pause
      if (typeof document !== 'undefined' && document.hidden) {
        AudioEvent(KissanEventType.AGRINEWS_AUDIO_TAB_PAUSE);
        playerRef.current?.getInternalPlayer()?.pause?.();
        setIsPlaying(false);
        return;
      }

      const currentTime = Math.floor(progress.playedSeconds);

      // Fire progress events at specific intervals
      if (currentTime >= 11 && !trackedProgressRef.current.has(11)) {
        AudioEvent(KissanEventType.AUTO_PLAY_AUDIO_PLAYONE, currentTime);
        trackedProgressRef.current.add(11);
      }
      if (currentTime >= 20 && !trackedProgressRef.current.has(20)) {
        AudioEvent(KissanEventType.AUTO_PLAY_AUDIO_PLAYTWENTY, currentTime);
        trackedProgressRef.current.add(20);
      }
      if (currentTime >= 30 && !trackedProgressRef.current.has(30)) {
        AudioEvent(KissanEventType.AUTO_PLAY_AUDIO_PLAYTHIRTY, currentTime);
        trackedProgressRef.current.add(30);
      }

      // Every 10 seconds
      if (
        currentTime > 0 &&
        currentTime % 10 === 0 &&
        !trackedProgressRef.current.has(currentTime)
      ) {
        AudioEvent(KissanEventType.AUTO_PLAY_AUDIO_PLAYPROGRESS, currentTime);
        trackedProgressRef.current.add(currentTime);
      }
    },
    [isPlaying]
  );

  return (
    <XStack
      space="$2"
      alignItems="center"
      background={'$primary'}
      width={36}
      height={24}
      borderRadius={16}
    >
      <Pressable onPress={handlePlayPause} disabled={loading}>
        {loading ? (
          <Spinner size="small" />
        ) : isMuteIconShow ? (
          <XStack justifyContent="center" alignItems="center" width={40} height={40}>
            {index === currentVisibleIndex && isActuallyPlaying ? (
              <Volume2 color="#fff" size={20} />
            ) : (
              <VolumeOff color="#fff" size={20} />
            )}
          </XStack>
        ) : (
          <XStack
            background={'$primary'}
            borderRadius={16}
            paddingVertical={10}
            paddingHorizontal={12}
          >
            {index === currentVisibleIndex && isActuallyPlaying ? (
              <Volume2 color="#fff" size={30} />
            ) : (
              <VolumeOff color="#fff" size={30} />
            )}
          </XStack>
        )}
      </Pressable>
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={isPlaying}
        controls={false}
        width="0"
        height="0"
        style={{ display: 'none' }}
        onDuration={(duration: number) => {
          durationRef.current = Math.floor(duration);
        }}
        onPlay={() => {
          setIsActuallyPlaying(true);
        }}
        onPause={() => {
          setIsActuallyPlaying(false);
        }}
        onEnded={() => {
          setIsActuallyPlaying(false);
          setIsPlaying(false);
          AudioEvent(KissanEventType.PODCAST_COMPLETED, durationRef.current);
          onAudioComplete?.();
          setCurrentPlayIndex?.(index + 1);
        }}
        onProgress={handleProgress}
      />
    </XStack>
  );
};
